/* global PUB_SUB_EVENTS, subscribe */

if (!customElements.get('product-purchase-panel')) {
  customElements.define(
    'product-purchase-panel',
    class ProductPurchasePanel extends HTMLElement {
      connectedCallback() {
        this.handleClick = this.handleClick.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.scheduleSync = this.scheduleSync.bind(this);

        this.addEventListener('click', this.handleClick);
        this.addEventListener('change', this.handleChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.scheduleSync);
          this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.scheduleSync);
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.scheduleSync);
        }

        this.submitObserver = new MutationObserver(this.scheduleSync);
        this.observeSubmitButton();
        this.sync();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('change', this.handleChange);
        this.variantChangeUnsubscriber?.();
        this.quantityUpdateUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.submitObserver?.disconnect();
        cancelAnimationFrame(this.syncFrame);
      }

      get quantityInput() {
        return this.querySelector('.quantity__input');
      }

      get submitButton() {
        return this.querySelector('.product-form__submit');
      }

      get tierButtons() {
        return Array.from(this.querySelectorAll('[data-quantity-tier]'));
      }

      observeSubmitButton() {
        this.submitObserver?.disconnect();
        if (this.submitButton) {
          this.submitObserver.observe(this.submitButton, {
            attributes: true,
            attributeFilter: ['aria-disabled', 'disabled'],
          });
        }
      }

      scheduleSync() {
        cancelAnimationFrame(this.syncFrame);
        this.syncFrame = requestAnimationFrame(() => {
          this.observeSubmitButton();
          this.sync();
        });
      }

      handleChange(event) {
        if (event.target === this.quantityInput) this.sync();
      }

      handleClick(event) {
        const tierButton = event.target.closest('[data-quantity-tier]');
        if (!tierButton || !this.contains(tierButton) || tierButton.disabled) return;

        const input = this.quantityInput;
        const threshold = this.toInteger(tierButton.dataset.quantityTier);
        if (!input || !threshold) return;

        const cartQuantity = this.toInteger(input.dataset.cartQuantity, 0);
        const minimum = this.toInteger(input.min || input.dataset.min, 1);
        const increment = this.toInteger(input.step, 1);
        const maximum = this.toInteger(input.max, null);
        const additionalNeeded = Math.max(minimum, threshold - cartQuantity);
        let nextValue = this.alignToIncrement(additionalNeeded, minimum, increment);

        if (maximum !== null) nextValue = Math.min(nextValue, maximum);
        if (cartQuantity + nextValue < threshold) return;

        input.value = nextValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        this.announceSelection(tierButton, threshold);
        this.sync();
      }

      sync() {
        const input = this.quantityInput;
        if (!input) return;

        const cartQuantity = this.toInteger(input.dataset.cartQuantity, 0);
        const enteredQuantity = this.toInteger(input.value, this.toInteger(input.min, 1));
        const totalQuantity = cartQuantity + enteredQuantity;
        const minimum = this.toInteger(input.min || input.dataset.min, 1);
        const increment = this.toInteger(input.step, 1);
        const maximum = this.toInteger(input.max, null);
        const purchaseUnavailable = Boolean(
          this.submitButton?.disabled ||
            (this.submitButton?.getAttribute('aria-disabled') === 'true' &&
              this.submitButton?.dataset.purchaseLimitDisabled !== 'true')
        );
        const purchaseLimitReached = maximum !== null && maximum <= 0;
        const quantityUnavailable = purchaseUnavailable || purchaseLimitReached;
        let activeButton = null;

        input.disabled = quantityUnavailable;
        this.querySelectorAll('.quantity__button').forEach((button) => {
          button.disabled = quantityUnavailable;
        });
        this.toggleLimitState(purchaseLimitReached);

        this.tierButtons.forEach((button) => {
          const threshold = this.toInteger(button.dataset.quantityTier);
          const additionalNeeded = Math.max(minimum, threshold - cartQuantity);
          const alignedQuantity = this.alignToIncrement(additionalNeeded, minimum, increment);
          const cannotReachTier = maximum !== null && cartQuantity < threshold && alignedQuantity > maximum;

          button.disabled = quantityUnavailable || cannotReachTier;
          button.setAttribute('aria-pressed', 'false');
          if (!button.disabled && threshold <= totalQuantity) activeButton = button;
        });

        activeButton?.setAttribute('aria-pressed', 'true');
        this.updateTierNudge(totalQuantity);
      }

      updateTierNudge(totalQuantity) {
        const nudge = this.querySelector('[data-purchase-tier-nudge]');
        if (!nudge) return;

        const sortedTiers = this.tierButtons
          .map((button) => ({ button, threshold: this.toInteger(button.dataset.quantityTier) }))
          .filter(({ threshold }) => threshold !== null)
          .sort((a, b) => a.threshold - b.threshold);
        const nextTier = sortedTiers.find(({ threshold }) => threshold > totalQuantity);

        if (nextTier && !nextTier.button.disabled) {
          nudge.hidden = false;
          const remaining = nextTier.threshold - totalQuantity;
          if (remaining === 1 && nudge.dataset.nudgeOne) {
            nudge.textContent = nudge.dataset.nudgeOne;
          } else if (nudge.dataset.nudgeOther) {
            nudge.textContent = nudge.dataset.nudgeOther.replace('[count]', String(remaining));
          }
          return;
        }

        nudge.textContent = '';
        nudge.hidden = true;
      }

      toggleLimitState(limitReached) {
        const submitButton = this.submitButton;
        if (!submitButton) return;

        this.toggleAttribute('data-purchase-limit-reached', limitReached);
        if (limitReached) {
          submitButton.dataset.purchaseLimitDisabled = 'true';
          if (submitButton.getAttribute('aria-disabled') !== 'true') {
            submitButton.setAttribute('aria-disabled', 'true');
          }
        } else if (submitButton.dataset.purchaseLimitDisabled === 'true') {
          delete submitButton.dataset.purchaseLimitDisabled;
          if (!submitButton.classList.contains('loading')) submitButton.removeAttribute('aria-disabled');
        }
      }

      announceSelection(button, threshold) {
        const status = this.querySelector('[data-purchase-tier-status]');
        const price = button.querySelector('[data-text]')?.textContent?.trim();
        if (status) status.textContent = `${threshold}+ flasker valgt${price ? `. ${price}` : ''}`;
      }

      alignToIncrement(quantity, minimum, increment) {
        const safeIncrement = Math.max(1, increment);
        return minimum + Math.ceil(Math.max(0, quantity - minimum) / safeIncrement) * safeIncrement;
      }

      toInteger(value, fallback = null) {
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) ? parsed : fallback;
      }
    }
  );
}
